import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import * as storage from '../utils/storage';
import * as eventApi from '../api/event';
import { formatEventDates, getEventSubText, daysToEvent } from '../utils/event';

export interface ProcessedEvent {
  id: number;
  name: string;
  start: string;
  end: string;
  app_enable?: string;
  start_promotion?: string;
  links?: eventApi.EventLink[];
  fee?: eventApi.EventFee;
  title_text: string;
  date_text: string;
}

interface EventContextType {
  events: ProcessedEvent[];
  selectedEventIndex: number | null;
  selectedEvent: ProcessedEvent | null;
  setSelectedEvent: (index: number) => void;
  reloadEvents: () => Promise<void>;
  isLoading: boolean;
}

const EventContext = createContext<EventContextType | null>(null);

interface EventProviderProps {
  children: ReactNode;
}

export function EventProvider({ children }: EventProviderProps) {
  const [events, setEvents] = useState<ProcessedEvent[]>([]);
  const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date(2000, 0, 1));

  const reloadEvents = useCallback(async () => {
    setIsLoading(true);
    const response = await eventApi.getEventList();

    if (!response.ok || !response.data) {
      console.error('Event load failed:', response.problem);
      setIsLoading(false);
      return;
    }

    if (response.data.error === null && response.data.event_types) {
      const eventTypes = response.data.event_types;
      let autoEventIndex = -1;
      let autoEventDays = 9999;
      const newEventList: ProcessedEvent[] = [];

      eventTypes.forEach((eventType, ix) => {
        if (eventType.event) {
          const event = eventType.event;
          const subText = getEventSubText(event.start, event.end);
          const titleText = subText ? `${event.name} (${subText})` : event.name;

          const processedEvent: ProcessedEvent = {
            ...event,
            title_text: titleText,
            date_text: formatEventDates(event.start, event.end),
          };

          let startDate = event.start;
          if (event.start_promotion) {
            startDate = event.start_promotion;
          } else if (event.app_enable) {
            startDate = event.app_enable;
          }

          const absDaysToEvent = daysToEvent(startDate, event.end);

          if (absDaysToEvent < autoEventDays) {
            autoEventIndex = ix;
            autoEventDays = absDaysToEvent;
          }

          newEventList.push(processedEvent);
        }
      });

      setEvents(newEventList);

      const hoursSinceLastRefresh = (Date.now() - lastRefreshTime.getTime()) / (60 * 60 * 1000);

      if (autoEventIndex > -1 && hoursSinceLastRefresh > 1) {
        setSelectedEventIndex(autoEventIndex);
        storage.setActiveEvent(autoEventIndex);
      }

      setLastRefreshTime(new Date());
    }

    setIsLoading(false);
  }, [lastRefreshTime]);

  useEffect(() => {
    const storedEventIndex = storage.getActiveEvent();
    if (storedEventIndex !== null) {
      setSelectedEventIndex(storedEventIndex);
    }
    reloadEvents();
  }, []);

  const setSelectedEvent = useCallback((index: number) => {
    if (index >= 0 && index < events.length) {
      setSelectedEventIndex(index);
      storage.setActiveEvent(index);
    }
  }, [events.length]);

  const selectedEvent = selectedEventIndex !== null && events[selectedEventIndex]
    ? events[selectedEventIndex]
    : null;

  const value: EventContextType = {
    events,
    selectedEventIndex,
    selectedEvent,
    setSelectedEvent,
    reloadEvents,
    isLoading,
  };

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}

export function useEvent(): EventContextType {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvent must be used within an EventProvider');
  }
  return context;
}

export default EventContext;
